import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';

import { ServiceService } from 'src/app/core/services/service';
import type {
  MappedService,
  ServiceSelectModeType,
} from 'src/app/models/service.model';
import { ServiceSelectMode } from 'src/app/models/service.model';
import {
  isOutsideElementClick,
  stopEvent,
} from 'src/app/shared/utils/dropdown.util';
import {
  InlineErrors,
  updateInlineErrors,
} from 'src/app/shared/utils/form-validation.util';
import { mapMultiServiceOption } from 'src/app/shared/utils/service-option.util';
import { AutofocusDirective } from 'src/app/shared/directives/autofocus.directive';
import {
  createBusinessDateValidator,
  getNextValidBusinessDate,
  isObservedHoliday,
  isValidBusinessDateAfterDays,
} from 'src/app/shared/utils/ticket-date-rules.util';
import { CalendarHeaderComponent } from '../calendar-header/calendar-header.component';

@Component({
  selector: 'app-select-multiple-services',
  templateUrl: './select-multiple-services.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    DatePipe,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatIconModule,
    AutofocusDirective,
  ],
})
export class SelectMultipleServicesComponent implements OnInit {
  @Input() formRef: UntypedFormGroup = new UntypedFormGroup({});

  @Input() mode: ServiceSelectModeType = ServiceSelectMode.Transfer;

  @Input() selectedServiceId: string;

  @Input() inlineErrors: InlineErrors = {};

  @Output() servicesLoaded = new EventEmitter(false);

  isLoadingServices = signal(true);

  serviceOptions = signal<MappedService[]>([]);

  selectedServicesTotal = signal(0);

  serviceDropdownOpen = signal<boolean[]>([]);

  serviceSearchTerms = signal<string[]>([]);

  startCalendarAt = signal(new Date());

  readonly customCalHeader = CalendarHeaderComponent;

  private serviceService = inject(ServiceService);

  private fb = inject(UntypedFormBuilder);

  private elementRef = inject(ElementRef);

  get isDisconnectMode(): boolean {
    return this.mode === ServiceSelectMode.Disconnect;
  }

  get addButtonLabel(): string {
    return this.isDisconnectMode
      ? 'Disconnect another service'
      : 'Add another service to transfer';
  }

  get noActiveServicesMessage(): string {
    return this.isDisconnectMode
      ? 'There are no active services available to disconnect.'
      : 'There are no active services available to transfer.';
  }

  get noMoreServicesMessage(): string {
    return this.isDisconnectMode
      ? 'There are no more services available to disconnect.'
      : 'There are no more services available to transfer.';
  }

  get serviceItemClass(): string {
    return this.isDisconnectMode
      ? 'mt-6 rounded border border-neutral-200 bg-gray-50 p-4'
      : 'line-item';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (isOutsideElementClick(this.elementRef, event)) {
      this.serviceDropdownOpen.update((openStates) =>
        openStates.map(() => false),
      );
      this.serviceSearchTerms.update((terms) => terms.map(() => ''));
    }
  }

  getFilteredServicesForIndex(index: number): MappedService[] {
    const term = (this.serviceSearchTerms()[index] ?? '').toLowerCase();
    if (!term) return this.serviceOptions();
    return this.serviceOptions().filter(
      (s) =>
        s.locationName?.toLowerCase().includes(term) ||
        s.nit?.toLowerCase().includes(term) ||
        s.product?.toLowerCase().includes(term),
    );
  }

  getSelectedServiceLabel(index: number): string {
    const serviceId = this.formRef.get(`services.${index}.serviceId`)
      ?.value as string;
    if (!serviceId) return '';
    return (
      this.serviceOptions().find((s) => s.id === serviceId)?.locationName ?? ''
    );
  }

  getSelectedServiceSubLabel(index: number): string {
    const serviceId = this.formRef.get(`services.${index}.serviceId`)
      ?.value as string;
    if (!serviceId) return '';
    const service = this.serviceOptions().find((s) => s.id === serviceId);
    return service ? `${service.nit} · ${service.product}` : '';
  }

  toggleServiceDropdown(index: number, event: Event): void {
    stopEvent(event);
    this.serviceDropdownOpen.update((openStates) =>
      openStates.map((isOpen: boolean, i: number) =>
        i === index ? !isOpen : false,
      ),
    );
  }

  selectServiceOption(serviceId: string, index: number, event: Event): void {
    stopEvent(event);
    this.onServiceSelected(serviceId, index);
    this.serviceDropdownOpen.update((openStates) =>
      openStates.map((isOpen, i) => (i === index ? false : isOpen)),
    );
    this.serviceSearchTerms.update((terms) =>
      terms.map((term, i) => (i === index ? '' : term)),
    );
  }

  onServiceSearchInput(index: number, event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.serviceSearchTerms.update((terms) =>
      terms.map((term, i) => (i === index ? value : term)),
    );
  }

  private initializeServiceUiState(count: number): void {
    this.serviceDropdownOpen.set(Array(count).fill(false));
    this.serviceSearchTerms.set(Array(count).fill(''));
  }

  private addServiceUiState(): void {
    this.serviceDropdownOpen.update((openStates) => [...openStates, false]);
    this.serviceSearchTerms.update((terms) => [...terms, '']);
  }

  private removeServiceUiState(index: number): void {
    this.serviceDropdownOpen.update((openStates) =>
      openStates.filter((_: boolean, i: number) => i !== index),
    );
    this.serviceSearchTerms.update((terms) =>
      terms.filter((_: string, i: number) => i !== index),
    );
  }

  ngOnInit(): void {
    this.startCalendarAt.set(this.getNextValidDate());
    this.serviceService.getServicesInService().subscribe((data) => {
      if (data) {
        this.servicesLoaded.emit(true);
        const mappedServices = data
          .map(mapMultiServiceOption)
          .filter((service): service is MappedService => !!service);
        this.serviceOptions.set(mappedServices);

        if (mappedServices.length === 1) {
          this.formRef
            .get(`services.${0}.locationId`)
            .setValue(`${mappedServices[0].locationId}`);
          this.formRef
            .get(`services.${0}.serviceId`)
            .setValue(`${mappedServices[0].id}`);
          this.formRef
            .get(`services.${0}.locationAddress`)
            .setValue(`${mappedServices[0].locationName}`);
        }

        if (this.selectedServiceId?.length) {
          const selectedService = mappedServices.find(
            (service) => service.id === this.selectedServiceId,
          );
          if (selectedService) {
            this.formRef
              .get(`services.${0}.identifyIssueBy`)
              .setValue('service');
            this.formRef
              .get(`services.${0}.locationId`)
              .setValue(`${selectedService.locationId}`);
            this.formRef
              .get(`services.${0}.serviceId`)
              .setValue(`${selectedService.id}`);
            this.formRef
              .get(`services.${0}.locationAddress`)
              .setValue(`${selectedService.locationName}`);
          }
        }

        this.isLoadingServices.set(false);
        const count = (this.formRef.get('services') as UntypedFormArray)
          .controls.length;
        this.initializeServiceUiState(count);
        this.canSubmit();
      } else {
        this.servicesLoaded.emit(true);
        this.isLoadingServices.set(false);
      }
    });
  }

  onServiceSelected(serviceId: string, index: number): void {
    if (serviceId !== null) {
      const locationAddress = this.serviceOptions().filter(
        (service) => service.id === serviceId,
      );

      this.formRef
        .get(`services.${index}.locationAddress`)
        .setValue(`${locationAddress[0].locationName}`);
      this.formRef.get(`services.${index}.serviceId`).setValue(serviceId);
      this.formRef
        .get(`services.${index}.locationId`)
        .setValue(locationAddress[0].locationId);

      this.updateServiceDisabledAttribute();
    }
  }

  updateServiceDisabledAttribute(): void {
    const servicesSelected: string[] = (
      this.formRef.get('services').value as any[]
    )
      .map((el) => el.serviceId)
      .filter(Boolean);

    this.selectedServicesTotal.set(servicesSelected.length);

    this.serviceOptions.update((services) =>
      services.map((service) => ({
        ...service,
        disabled: servicesSelected.includes(service.id),
      })),
    );
  }

  canSubmit(): boolean {
    return updateInlineErrors(
      this.formRef,
      this.inlineErrors,
      {},
      {
        clearValidWith: 'delete',
      },
    );
  }

  removeServicesFormControl(i: number): void {
    const servicesArray = this.formRef.controls.services as UntypedFormArray;
    servicesArray.removeAt(i);
    this.removeServiceUiState(i);
    this.updateServiceDisabledAttribute();
  }

  addServicesFormControl(): void {
    const servicesArray = this.formRef.controls.services as UntypedFormArray;
    const arrayLength = servicesArray.length;

    const groupConfig: Record<string, any> = {
      identifyIssueBy: ['location'],
      serviceId: new UntypedFormControl(null, Validators.required),
      locationId: [null, Validators.required],
      locationAddress: [null],
      servicesByLocation: this.fb.array([
        this.fb.group({
          id: this.fb.control(''),
          disabled: this.fb.control(''),
          nit: this.fb.control(''),
          product: this.fb.control(''),
          locationId: this.fb.control(''),
          locationName: this.fb.control(''),
        }),
      ]),
    };

    if (this.isDisconnectMode) {
      groupConfig.desiredDate = this.fb.control(null, [
        Validators.required,
        this.validateDisconnectDate,
      ]);
    }

    const newServiceGroup: UntypedFormGroup = this.fb.group(groupConfig);
    servicesArray.insert(arrayLength, newServiceGroup);
    this.addServiceUiState();
    this.updateServiceDisabledAttribute();
  }

  validateDisconnectDate = createBusinessDateValidator(30);

  filterDates = (d: Date): boolean => isValidBusinessDateAfterDays(d, 30);

  isHoliday(calendarDate: Date): boolean {
    return isObservedHoliday(calendarDate);
  }

  getNextValidDate(): Date {
    return getNextValidBusinessDate(30);
  }
}
